<script setup lang="ts">
import Checkbox from '@/components/Checkbox.vue'
import RateBoardModeInfo from '@/components/assistant/RateBoardModeInfo.vue'
import Tooltip from '@/components/Tooltip.vue'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import {DripInterval} from '@/client/mentor/CreditUsageClient'
import {Menu as VMenu} from 'floating-vue'
import {PERCENT_DIGIT_FMT, useNumberFormatter} from '@/composable/useNumberFormatter'
import {REQUEST_CREDITS_URL} from '@/components/assistant/constants'
import {computed, ref, toRef} from 'vue'
import {useAssistantDebugSettings} from '@/components/assistant/composables/useAssistantDebugSettings'
import {useAssistantTierName} from '@/components/assistant/composables/useAssistantTierName'
import {useLanguageMapping} from '@/composables/useLanguageMapping'

const DRIP_BOOST_LOCAL_KEY: Record<DripInterval, string> = {
  [DripInterval.DAILY]: 'assistant.creditUsage.dripBoost.daily', // t('assistant.creditUsage.dripBoost.daily')
  [DripInterval.WEEKLY]: 'assistant.creditUsage.dripBoost.weekly', // t('assistant.creditUsage.dripBoost.weekly')
}

const DRIP_EXPLAIN_LOCAL_KEY: Record<DripInterval, string> = {
  [DripInterval.DAILY]: 'assistant.creditUsage.tooltip.dripDescription.daily', // t('assistant.creditUsage.tooltip.dripDescription.daily')
  [DripInterval.WEEKLY]: 'assistant.creditUsage.tooltip.dripDescription.weekly', // t('assistant.creditUsage.tooltip.dripDescription.weekly')
}

const [percentageFormatter] = useNumberFormatter(PERCENT_DIGIT_FMT)

const props = defineProps<{
  creditUsage?: CreditUsage
}>()

const visibleMenu = ref(false)

const creditUsageValues = computed(() => {
  const usage = props.creditUsage
  if (!(usage && usage.isActiveTier && usage.periodCreditsIncludingTopUps > 0)) return undefined
  const consumedRatio = limitValue(usage.consumedCredits / usage.periodCreditsIncludingTopUps)
  const releasedRatio = limitValue(usage.releasedCredits / usage.periodCreditsIncludingTopUps)
  return {
    consumedTotalPercent: percentageFormatter.value.format(consumedRatio),
    releasedCreditsPercent: percentageFormatter.value.format(releasedRatio),
    consumedTotalWidth: formatCssPercentage(consumedRatio),
    releasedCreditsWidth: formatCssPercentage(releasedRatio),
  }
})

const tierName = useAssistantTierName(toRef(() => props.creditUsage?.tier))

const requestCreditsUrl = useLanguageMapping(REQUEST_CREDITS_URL)
const {debugMode, isAdmin, showInvokedTools} = useAssistantDebugSettings()

function formatCssPercentage(value: number): string {
  return `${Math.round(value * 100)}%`
}

function limitValue(value: number, min: number = 0, max: number = 1): number {
  return Math.min(Math.max(value, min), max)
}
</script>

<template>
  <div class="p-4 bg-new-neutral-0 rounded-2xl flex flex-col gap-2 text-new-neutral-700 text-sm">
    <div class="flex flex-row items-center gap-2 uppercase">
      <span>{{ $t('assistant.creditUsage.usage') }}</span>
      <div
        v-if="creditUsageValues"
        class="px-2 py-0.5 bg-new-tertiary-200 rounded-full text-xs tracking-widest text-new-secondary-default font-bold"
      >
        {{ tierName }}
      </div>

      <div class="ml-auto flex items-center gap-2">
        <Tooltip v-if="creditUsageValues">
          <i class="fa-solid fa-circle-question text-new-neutral-500"></i>

          <template #tooltip-content>
            <div class="flex flex-col gap-1">
              <div class="font-bold">{{ $t('assistant.creditUsage.tooltip.title') }}</div>

              <p>{{ $t('assistant.creditUsage.tooltip.description') }}</p>

              <div class="grid grid-cols-[auto_1fr] gap-x-1 gap-y-0.5">
                <span>•</span>
                <div>
                  <span class="font-bold">{{ $t('assistant.creditUsage.usedCredits') }}:</span>
                  {{ $t('assistant.creditUsage.tooltip.usedCreditsDescription') }}
                </div>

                <span>•</span>
                <div>
                  <span class="font-bold">{{ $t('assistant.creditUsage.availableCredits') }}:</span>
                  {{ $t('assistant.creditUsage.tooltip.availableCreditsDescription') }}
                </div>

                <template v-if="creditUsage?.dripInterval">
                  <span>•</span>
                  <div>
                    <span class="font-bold">{{ $t(DRIP_BOOST_LOCAL_KEY[creditUsage?.dripInterval]) }}:</span>
                    {{ $t(DRIP_EXPLAIN_LOCAL_KEY[creditUsage?.dripInterval]) }}
                  </div>
                </template>
              </div>
            </div>
          </template>
        </Tooltip>

        <VMenu v-if="isAdmin" v-model:shown="visibleMenu" auto-hide :show-triggers="['click', 'touch']">
          <button v-tt="'Debug settings'" class="text-new-neutral-500 hover:text-new-primary-default" type="button">
            <i class="fa-solid fa-gear"></i>
          </button>

          <template #popper>
            <div class="flex min-w-56 flex-col gap-3 p-3 normal-case">
              <Checkbox label="Show debug information" :value="debugMode" @change="(value) => (debugMode = value)" />
              <Checkbox label="Show invoked tools" :value="showInvokedTools" @change="(value) => (showInvokedTools = value)" />
            </div>
          </template>
        </VMenu>
      </div>
    </div>

    <template v-if="creditUsageValues">
      <!-- Consumption -->
      <div v-if="creditUsageValues.consumedTotalPercent">
        <span class="text-xl font-semi-bold">{{ creditUsageValues.consumedTotalPercent }}</span>
        {{ $t('assistant.creditUsage.ofMonthlyCreditsUsed') }}
      </div>

      <!-- Consumption bar -->
      <div>
        <div class="w-full h-2 rounded-full border-new-sm border-new-neutral-100 relative">
          <div
            class="absolute rounded-full bg-new-neutral-100"
            :style="{width: creditUsageValues.releasedCreditsWidth, height: '100%'}"
          ></div>
          <div
            class="absolute rounded-full bg-new-secondary-default"
            :style="{width: creditUsageValues.consumedTotalWidth, height: '100%'}"
          ></div>
        </div>
        <RateBoardModeInfo />
      </div>

      <div class="flex flex-col">
        <div v-if="creditUsageValues.consumedTotalPercent" class="flex flex-row items-center gap-2 whitespace-nowrap">
          <div class="w-2 h-2 rounded-full bg-new-secondary-default"></div>
          <div>{{ $t('assistant.creditUsage.usedCredits') }}</div>
        </div>
        <div class="flex flex-row items-center gap-2 whitespace-nowrap">
          <div class="w-2 h-2 rounded-full bg-new-neutral-100"></div>
          <div>{{ $t('assistant.creditUsage.availableCredits') }}</div>
        </div>
        <div v-if="creditUsage?.dripInterval" class="flex flex-row items-center gap-2 whitespace-nowrap">
          <div class="w-2 h-2 rounded-full border-new-sm border-new-neutral-100"></div>
          <div>{{ $t(DRIP_BOOST_LOCAL_KEY[creditUsage?.dripInterval]) }}</div>
        </div>
      </div>

      <!-- Rule -->
      <hr />

      <!-- Request more AI usage -->
      <div class="flex flex-col">
        <span>{{
          creditUsage?.isActiveTier
            ? $t('assistant.creditUsage.requestMoreCredits')
            : $t('assistant.creditUsage.noCreditsInThisTier', {tier: tierName})
        }}</span>
        <a :href="requestCreditsUrl" rel="noopener noreferrer" target="_blank">{{
          creditUsage?.isActiveTier ? $t('assistant.creditUsage.requestItNow') : $t('assistant.creditUsage.requestAiManager')
        }}</a>
      </div>
    </template>

    <template v-else>
      <!-- Request AI usage -->
      <div class="flex flex-col">
        <span>{{ $t('assistant.creditUsage.noCreditsInThisTier', {tier: tierName}) }}</span>
        <a :href="requestCreditsUrl" rel="noopener noreferrer" target="_blank">{{
          $t('assistant.creditUsage.requestAiManager')
        }}</a>
      </div>
    </template>
  </div>
</template>
